import { StatisticType } from '../types/dashboard.type';
import { EsgMonthlyMetricType } from '../types/esg-monthly-metric.type';

/**
 * Whether a rising value is good news. Drives delta colour only — the arrow
 * always follows the sign, and the text carries the value, so colour is never
 * the sole signal.
 */
export type MetricPolarity = 'higher_is_better' | 'lower_is_better' | 'neutral';

export type DeltaTone = 'positive' | 'negative' | 'neutral';

export function deltaTone(delta: number | null | undefined, polarity: MetricPolarity): DeltaTone {
  if (typeof delta !== 'number' || !Number.isFinite(delta) || delta === 0) return 'neutral';
  if (polarity === 'neutral') return 'neutral';
  const rising = delta > 0;
  return (polarity === 'higher_is_better') === rising ? 'positive' : 'negative';
}

/** ESG Monthly number cards. Resource use is lower-is-better (GRI 302/303 intent). */
export const ESG_METRIC_POLARITY: Record<EsgMonthlyMetricType, MetricPolarity> = {
  [EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED]: 'higher_is_better',
  [EsgMonthlyMetricType.WASTE_DIVERSION_RATE]: 'higher_is_better',
  [EsgMonthlyMetricType.TOTAL_CO2]: 'higher_is_better',
  [EsgMonthlyMetricType.RECYCLED_WASTE_BREAKDOWN]: 'neutral',
  [EsgMonthlyMetricType.CARBON_AVOIDED_BY_STREAM]: 'neutral',
  [EsgMonthlyMetricType.CARBON_AVOIDED_BY_MATERIAL]: 'neutral',
  [EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT]: 'neutral',
  [EsgMonthlyMetricType.TRAINING_HOURS_TOTAL]: 'higher_is_better',
  [EsgMonthlyMetricType.HEALTH_SAFETY]: 'lower_is_better',
  [EsgMonthlyMetricType.WATER_USE]: 'lower_is_better',
  [EsgMonthlyMetricType.ELECTRICITY_USE]: 'lower_is_better',
  [EsgMonthlyMetricType.OIL_USE]: 'lower_is_better',
  [EsgMonthlyMetricType.GAS_USE]: 'lower_is_better',
};

/** Operational stats; anything not listed is treated as neutral. */
export const OPERATIONAL_STAT_POLARITY: Partial<Record<StatisticType, MetricPolarity>> = {
  [StatisticType.TIME_FROM_FULL_TO_COLLECTION]: 'lower_is_better',
  [StatisticType.TIME_FROM_COLLECTION_TO_DELIVERY]: 'lower_is_better',
  [StatisticType.AVERAGE_PROCESSING_TIME]: 'lower_is_better',
  [StatisticType.REJECTED_WASTE_PERCENTAGE]: 'lower_is_better',
  [StatisticType.FUEL_CONSUMPTION_PER_TRIP]: 'lower_is_better',
  [StatisticType.EMERGENCY_COLLECTION_REQUESTS]: 'lower_is_better',
  [StatisticType.QUALITY_COMPLIANCE_RATE]: 'higher_is_better',
  [StatisticType.MAINTENANCE_SCHEDULE_COMPLIANCE]: 'higher_is_better',
  [StatisticType.DRIVER_PERFORMANCE_SCORE]: 'higher_is_better',
  [StatisticType.CUSTOMER_SATISFACTION_SCORE]: 'higher_is_better',
  [StatisticType.WASTE_QUALITY_RATING]: 'higher_is_better',
  [StatisticType.AVERAGE_ROUTE_EFFICIENCY]: 'higher_is_better',
  [StatisticType.WASTE_COMPACTION_EFFICIENCY]: 'higher_is_better',
  [StatisticType.SUSTAINABLE_SUPPLY]: 'higher_is_better',
  [StatisticType.TOTAL_REVENUE]: 'higher_is_better',
};

export function operationalStatPolarity(stat: StatisticType | string): MetricPolarity {
  return OPERATIONAL_STAT_POLARITY[stat as StatisticType] ?? 'neutral';
}

/** Minimal shape of a KPI limit (see `stores/kpi.store.ts`). */
export interface KpiLimitLike {
  name: string;
  isEnabled: boolean;
}

/** `TOTAL_ORDERS`, `total_orders`, `Total Orders` → `total_orders`. */
export function normalizeMetricKey(key: string): string {
  return key
    .trim()
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

/**
 * Enabled KPI limit for a metric. Matches on the canonical metric key so it
 * works in every UI language (titles are translated, keys are not).
 */
export function findEnabledKpiLimit<T extends KpiLimitLike>(
  limits: readonly T[],
  metricKey: string | null | undefined,
): T | undefined {
  if (!metricKey) return undefined;
  const wanted = normalizeMetricKey(metricKey);
  return limits.find((l) => l.isEnabled && normalizeMetricKey(l.name) === wanted);
}
